import type StripeService from '@/Services/Subscription/StripeService';
import * as Sentry from '@sentry/vue';
import type { Location } from 'vue-router';

type RouteQuery = Location['query'] | undefined;

type StripeServiceLike = Pick<StripeService, 'stripe' | 'initStripe'> | null | undefined;

/** Query keys stripped from the URL after handling a redirect-based add. */
export const ASYNC_PAYMENT_METHOD_ADD_QUERY_KEYS = [
  'returningFromPaypal',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
] as const;

/**
 * PayPal and other redirect-based adds return via `/processing` and land on
 * billing with query params — they never emit `update-payment-method`.
 */
export function isReturningFromAsyncPaymentMethodAdd(query: RouteQuery): boolean {
  return query?.returningFromPaypal === 'true' && query?.redirect_status === 'succeeded';
}

/**
 * Failed or aborted redirect-based adds still land on billing with
 * `returningFromPaypal=true` and a non-success `redirect_status`. Callers
 * should surface an error and clear the redirect query params.
 */
export function isFailedAsyncPaymentMethodAddReturn(query: RouteQuery): boolean {
  const redirectStatus = query?.redirect_status;
  return (
    query?.returningFromPaypal === 'true' &&
    typeof redirectStatus === 'string' &&
    redirectStatus.length > 0 &&
    redirectStatus !== 'succeeded'
  );
}

export function buildQueryWithoutAsyncPaymentMethodAddParams(query: RouteQuery): NonNullable<RouteQuery> {
  const cleared = { ...(query ?? {}) };

  for (const key of ASYNC_PAYMENT_METHOD_ADD_QUERY_KEYS) {
    cleared[key] = undefined;
  }

  return cleared;
}

export async function clearAsyncPaymentMethodAddQueryParams(
  router: { replace: (location: { query: NonNullable<RouteQuery> }) => Promise<unknown> },
  route: { query: RouteQuery }
): Promise<void> {
  await router.replace({
    query: buildQueryWithoutAsyncPaymentMethodAddParams(route.query),
  });
}

/**
 * Reads the Stripe payment-method id from the setup-intent client secret left
 * on the URL by the redirect flow, so we can highlight the exact method that
 * was just added instead of guessing from creation date.
 *
 * Stripe init / setup-intent lookup is best-effort for highlighting — callers
 * must still load the list and clean up redirect query params on failure.
 */
export async function resolveAddedPaymentMethodIdFromQuery(
  query: RouteQuery,
  stripeService: StripeServiceLike,
  stripeInstanceCountry: string | null | undefined
): Promise<string | null> {
  const clientSecret = query?.setup_intent_client_secret;
  if (typeof clientSecret !== 'string' || !clientSecret) {
    return null;
  }

  if (!stripeService) {
    return null;
  }

  try {
    if (!stripeService.stripe) {
      if (!stripeInstanceCountry) {
        return null;
      }
      await stripeService.initStripe(stripeInstanceCountry);
    }

    if (!stripeService.stripe) {
      return null;
    }

    const { setupIntent, error } = await stripeService.stripe.retrieveSetupIntent(clientSecret);
    if (error || !setupIntent?.payment_method) {
      return null;
    }

    const paymentMethod = setupIntent.payment_method;
    return typeof paymentMethod === 'string' ? paymentMethod : paymentMethod.id;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
}
